import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faBell,
  faClock,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../config";
import { useNavigate } from "react-router-dom";
import Header from "./component/Header";
import Swal from "sweetalert2";
import InputMask from "react-input-mask";
import StateMunicipalityDropdown from "./component/StateMunicipalityDropdown";
import { Button, Section, Card } from "../components/ui";
import PublicFooter from "../components/layout/PublicFooter";
import SEO from "../components/SEO";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "RMT - Renew My Tags",
  "url": "https://renewmytags.com",
  "description": "Automated vehicle tag renewal service. We monitor your registration, renew your plates automatically, and deliver new tags to your door.",
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "support@renewmytags.com",
    "contactType": "customer service"
  }
};

const Home = () => {
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [plateNo, setPlateNo] = useState("");
  const [onboardingId, setOnboardingId] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [rawPhoneNumber, setRawPhoneNumber] = useState("");

  const handleFieldBlur = (field, value) => {
    if (!onboardingId && onboardingId <= 0) {
      fetch(API_URL + "/onboarding/generate/initial")
        .then((response) => response.json())
        .then((data) => {
          setOnboardingId(data.onboardingId);
          updateOnboardingField(data.onboardingId, field, value);
        })
        .catch((error) => console.error("Error:", error));
    } else {
      updateOnboardingField(onboardingId, field, value);
    }
  };

  const updateOnboardingField = (onboardingId, field, value) => {
    fetch(API_URL + "/onboarding/update/initial", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        onboarding_id: onboardingId,
        plate_number: plateNo,
        email,
        phone: rawPhoneNumber,
        municipality_id: selectedState ? selectedState.value : null,
      }),
    })
      .then((response) => response.json())
      .then((data) => {})
      .catch((error) => console.error("Error:", error));
  };

  useEffect(() => {
    fetch(API_URL + "/municipalities")
      .then((response) => response.json())
      .then((data) => {
        setStateOptions(data);
      })
      .catch((error) => {
        console.error("Error fetching municipalities:", error);
      });
  }, []);

  const handlePhoneNumberChange = (e) => {
    const formattedPhoneNumber = e.target.value;
    setPhone(formattedPhoneNumber);
    const rawPhoneNumber = formattedPhoneNumber.replace(/\D/g, "");
    setRawPhoneNumber(rawPhoneNumber);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = {};
    if (!selectedState) {
      validationErrors.state = "Please select a county.";
    }
    if (!plateNo) {
      validationErrors.plateNo = "Please enter a plate number.";
    }
    if (!email) {
      validationErrors.email = "Please enter an email.";
    } else if (!validateEmail(email)) {
      validationErrors.email = "Please enter a valid email.";
    }
    if (!phone) {
      validationErrors.phone = "Please enter a phone number.";
    } else if (!validatePhone(phone)) {
      validationErrors.phone = "Please enter a valid phone number.";
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const combinedErrorMessage = Object.values(validationErrors).join("<br>");
      Swal.fire({
        icon: "error",
        title: "Please fix the following issues:",
        html: `${combinedErrorMessage}`,
        showCloseButton: true,
      });
      return;
    } else {
      fetch(API_URL + "/onboarding", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          onboarding_id: onboardingId,
          plate_number: plateNo,
          email,
          phone: rawPhoneNumber,
          municipality_id: selectedState ? selectedState.value : null,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.errors) {
            setErrors(data.errors);
            const errorMessages = Object.values(data.errors).join("\n");
            if (data.errors.email) {
              const loginButton = document.getElementById("loginButtonTop");
              if (loginButton) {
                loginButton.click();
              }
              Swal.fire({
                icon: "error",
                title: "Email already in use.",
                html: "Please login or use a different email.",
                timer: 2000,
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Submission failed. Error(s):",
                html: `${errorMessages}`,
                showCloseButton: true,
              });
            }
          } else {
            setSelectedState(null);
            setPlateNo("");
            setEmail("");
            setPhone("");
            setErrors({});
            const encryptedOnboardingId = data.encrypted_onboarding_id;
            navigate(`/signup-wizard/${encryptedOnboardingId}`);
          }
        })
        .catch((error) => {
          console.error("Error submitting the form. Please try again later.", {
            autoClose: 3000,
          });
        });
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^\S+@\S+\.\S+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/;
    return phoneRegex.test(phone);
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      <SEO
        title="Automated Vehicle Tag Renewal Service"
        description="Never miss a car tag renewal deadline. RMT monitors your vehicle registration, renews your plates automatically, and delivers new tags to your door. Serving Tennessee, Michigan, Idaho, Florida, and more."
        canonical="/"
        structuredData={organizationSchema}
      />
      <Header />

      {/* Hero Section */}
      <div className="tw-relative tw-overflow-hidden">
        {/* Background Image */}
        <div className="tw-absolute tw-inset-0">
          <img
            src={process.env.PUBLIC_URL + '/images/hero-plates.jpg'}
            alt="Collection of license plates from multiple US states"
            className="tw-w-full tw-h-full tw-object-cover"
          />
          <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-gray-900/90 tw-via-gray-900/75 tw-to-gray-900/60"></div>
        </div>

        <div className="tw-relative tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-pt-20 md:tw-pt-28 tw-pb-32 md:tw-pb-44">
          <div className="tw-text-center tw-max-w-3xl tw-mx-auto">
            <h1 className="tw-text-4xl md:tw-text-5xl tw-text-white tw-m-0 tw-tracking-tight">
              Vehicle Tag Renewal,{" "}
              <span className="tw-text-amber-400">Automated.</span>
            </h1>
            <p className="tw-text-lg md:tw-text-xl tw-text-gray-300 tw-mt-5 tw-mb-0 tw-max-w-2xl tw-mx-auto tw-leading-relaxed">
              Never miss a renewal deadline again. We monitor your tags and renew them automatically &mdash; so you can stay on the road, hassle-free.
            </p>
          </div>
        </div>
      </div>

      {/* Onboarding Form — overlapping hero */}
      <div className="tw-relative tw-z-10 -tw-mt-20 tw-mb-16 md:tw-mb-24">
        <div className="tw-max-w-3xl tw-mx-auto tw-px-4 sm:tw-px-6">
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-border tw-border-gray-100 tw-overflow-hidden">
            {/* Form header */}
            <div className="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-700 tw-px-10 md:tw-px-14 tw-py-6 tw-text-center">
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-2">
                <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-bg-white/20 tw-text-white tw-text-xs tw-font-bold">1</span>
                <span className="tw-text-primary-200 tw-text-sm">Step 1 of 3</span>
              </div>
              <h2 className="tw-text-2xl tw-text-white tw-m-0">
                Start your free registration
              </h2>
              <p className="tw-text-primary-200 tw-text-sm tw-m-0 tw-mt-1">Takes less than 2 minutes to get started.</p>
            </div>

            <div className="tw-p-10 md:tw-p-14">
            <form onSubmit={handleSubmit}>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-6">
                {/* County Select */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <label className="tw-text-sm tw-font-medium tw-text-gray-700">
                    State & County
                  </label>
                  <StateMunicipalityDropdown
                    data={stateOptions}
                    selectedMunicipality={selectedState}
                    setSelectedMunicipality={setSelectedState}
                  />
                </div>

                {/* Plate Number */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <label htmlFor="plateNo" className="tw-text-sm tw-font-medium tw-text-gray-700">
                    Plate Number
                  </label>
                  <input
                    type="text"
                    id="plateNo"
                    onBlur={() => handleFieldBlur("plate_number", plateNo)}
                    className={`tw-w-full tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100 ${errors.plateNo ? 'tw-border-error' : 'tw-border-gray-400'}`}
                    value={plateNo}
                    onChange={(e) =>
                      setPlateNo(
                        e.target.value
                          ? e.target.value.replace(/[^0-9a-zA-Z]+/gi, "")
                          : ""
                      )
                    }
                    placeholder="e.g. ABC1234"
                  />
                </div>

                {/* Email */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <label htmlFor="email" className="tw-text-sm tw-font-medium tw-text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    onBlur={() => handleFieldBlur("email", email)}
                    className={`tw-w-full tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100 ${errors.email ? 'tw-border-error' : 'tw-border-gray-400'}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>

                {/* Phone */}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <label htmlFor="phone" className="tw-text-sm tw-font-medium tw-text-gray-700">
                    Phone Number
                  </label>
                  <InputMask
                    mask="(999) 999-9999"
                    id="phone"
                    name="phone"
                    value={phone}
                    onBlur={() => handleFieldBlur("phone", phone)}
                    onChange={handlePhoneNumberChange}
                    className={`tw-w-full tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100 ${errors.phone ? 'tw-border-error' : 'tw-border-gray-400'}`}
                    placeholder="(555) 123-4567"
                    type="tel"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <div className="tw-mt-8 tw-text-center">
                <button
                  type="submit"
                  className="tw-w-full tw-bg-primary-600 hover:tw-bg-primary-700 tw-text-white tw-px-10 tw-py-4 tw-rounded-lg tw-text-lg tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-all tw-duration-200 tw-shadow-md hover:tw-shadow-lg"
                >
                  Start My Renewal
                  <FontAwesomeIcon icon={faArrowRight} className="tw-ml-2" />
                </button>
              </div>

              {/* Trust signals */}
              <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-6 tw-gap-y-2 tw-text-xs tw-text-gray-400">
                <div className="tw-flex tw-items-center tw-gap-1.5">
                  <svg className="tw-w-4 tw-h-4 tw-text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                  <span>256-bit SSL secured</span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1.5">
                  <svg className="tw-w-4 tw-h-4 tw-text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Setup in under 2 min</span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1.5">
                  <svg className="tw-w-4 tw-h-4 tw-text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Cancel anytime</span>
                </div>
              </div>
            </form>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Bar */}
      <Section background="tw-bg-gray-50" padding="tw-py-12">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-8 md:tw-gap-16">
          <div className="tw-text-center">
            <div className="tw-text-3xl tw-font-bold tw-text-primary-600">5,000+</div>
            <div className="tw-text-sm tw-text-gray-500 tw-mt-1">Renewals Processed</div>
          </div>
          <div className="tw-hidden md:tw-block tw-w-px tw-h-12 tw-bg-gray-300"></div>
          <div className="tw-text-center">
            <div className="tw-text-3xl tw-font-bold tw-text-primary-600">50+</div>
            <div className="tw-text-sm tw-text-gray-500 tw-mt-1">Counties Served</div>
          </div>
          <div className="tw-hidden md:tw-block tw-w-px tw-h-12 tw-bg-gray-300"></div>
          <div className="tw-text-center">
            <div className="tw-text-3xl tw-font-bold tw-text-primary-600">99.9%</div>
            <div className="tw-text-sm tw-text-gray-500 tw-mt-1">On-Time Renewal Rate</div>
          </div>
          <div className="tw-hidden md:tw-block tw-w-px tw-h-12 tw-bg-gray-300"></div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <svg className="tw-w-5 tw-h-5 tw-text-accent-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="tw-text-sm tw-font-semibold tw-text-gray-800">Secure Payments</div>
              <div className="tw-text-xs tw-text-gray-500">256-bit encryption</div>
            </div>
          </div>
        </div>
      </Section>

      {/* How It Works */}
      <Section padding="tw-py-16 md:tw-py-24">
        <div className="tw-text-center tw-mb-12">
          <h2 className="tw-text-4xl tw-text-gray-900 tw-m-0">How It Works</h2>
          <p className="tw-text-lg tw-text-gray-500 tw-mt-3 tw-mb-0 tw-max-w-xl tw-mx-auto">
            Three simple steps to never worry about tag renewal again.
          </p>
        </div>
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-8">
          {[
            {
              step: "1",
              title: "Sign Up",
              description: "Enter your plate information, select your county, and choose a payment method. It takes less than 5 minutes.",
            },
            {
              step: "2",
              title: "We Monitor",
              description: "We track your registration expiration dates and notify you 30 days before each renewal is due.",
            },
            {
              step: "3",
              title: "Auto-Renewed",
              description: "We process your renewal automatically and deliver your new tags directly to your door.",
            },
          ].map((item) => (
            <div key={item.step} className="tw-text-center tw-relative">
              <div className="tw-w-14 tw-h-14 tw-rounded-full tw-bg-primary-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-text-2xl tw-font-bold tw-mx-auto tw-mb-5 tw-shadow-lg tw-shadow-primary-200">
                {item.step}
              </div>
              <h3 className="tw-text-xl tw-text-gray-900 tw-m-0 tw-mb-2">{item.title}</h3>
              <p className="tw-text-sm tw-text-gray-500 tw-m-0 tw-leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Dashboard Preview — Track All Your Plates */}
      <Section background="tw-bg-gray-50" padding="tw-py-16 md:tw-py-24">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-12 tw-items-center">
          <div>
            <div className="tw-text-sm tw-font-semibold tw-text-primary-600 tw-uppercase tw-tracking-wide tw-mb-3">Your Dashboard</div>
            <h2 className="tw-text-3xl md:tw-text-4xl tw-text-gray-900 tw-m-0 tw-mb-4">Track all your plates in one place</h2>
            <p className="tw-text-lg tw-text-gray-600 tw-m-0 tw-mb-6 tw-leading-relaxed">
              See every vehicle at a glance — plate number, county, days until renewal, and status. Toggle auto-renewal on or off, change your preferred payment method, and add new plates anytime.
            </p>
            <ul className="tw-space-y-3 tw-m-0 tw-p-0 tw-list-none">
              {["Real-time renewal countdown for each plate", "One-click toggle to activate or pause auto-renewal", "Manage multiple vehicles from a single account"].map((item, i) => (
                <li key={i} className="tw-flex tw-items-start tw-gap-3">
                  <svg className="tw-w-5 tw-h-5 tw-text-green-500 tw-mt-0.5 tw-flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <span className="tw-text-sm tw-text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          {/* Dashboard mockup */}
          <div className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-border tw-border-gray-200 tw-overflow-hidden">
            <div className="tw-bg-gray-900 tw-px-4 tw-py-3 tw-flex tw-items-center tw-gap-2">
              <div className="tw-flex tw-gap-1.5">
                <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-red-500"></div>
                <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-yellow-500"></div>
                <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-green-500"></div>
              </div>
              <div className="tw-flex-1 tw-text-center tw-text-xs tw-text-gray-400">renewmytags.com/account</div>
            </div>
            <div className="tw-p-6">
              {/* Stat cards mini */}
              <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-mb-5">
                {[{ label: "Total Plates", value: "3", color: "tw-text-blue-600 tw-bg-blue-50" }, { label: "Next Renewal", value: "18d", color: "tw-text-amber-600 tw-bg-amber-50" }, { label: "Active", value: "3/3", color: "tw-text-green-600 tw-bg-green-50" }].map((s, i) => (
                  <div key={i} className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-center">
                    <div className={`tw-text-xl tw-font-bold ${s.color.split(' ')[0]}`}>{s.value}</div>
                    <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
              {/* Table mini */}
              <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <table className="tw-w-full tw-text-xs">
                  <thead><tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-gray-500 tw-font-semibold">Plate</th>
                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-gray-500 tw-font-semibold">County</th>
                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-gray-500 tw-font-semibold">Renewal</th>
                    <th className="tw-text-center tw-px-3 tw-py-2 tw-text-gray-500 tw-font-semibold">Status</th>
                  </tr></thead>
                  <tbody>
                    {[{ plate: "ABC1234", county: "Miami-Dade, FL", days: "18 days", active: true }, { plate: "XYZ5678", county: "Broward, FL", days: "45 days", active: true }, { plate: "DEF9012", county: "Palm Beach, FL", days: "92 days", active: true }].map((r, i) => (
                      <tr key={i} className="tw-border-b tw-border-gray-100">
                        <td className="tw-px-3 tw-py-2.5 tw-font-medium tw-text-blue-600">{r.plate}</td>
                        <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">{r.county}</td>
                        <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">{r.days}</td>
                        <td className="tw-px-3 tw-py-2.5 tw-text-center"><span className="tw-inline-block tw-w-8 tw-h-4 tw-rounded-full tw-bg-green-500"></span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Payment & Notifications Preview */}
      <Section padding="tw-py-16 md:tw-py-24">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-12 tw-items-center">
          {/* Payment mockup */}
          <div className="tw-order-2 lg:tw-order-1 tw-bg-white tw-rounded-xl tw-shadow-2xl tw-border tw-border-gray-200 tw-overflow-hidden">
            <div className="tw-bg-gray-900 tw-px-4 tw-py-3 tw-flex tw-items-center tw-gap-2">
              <div className="tw-flex tw-gap-1.5">
                <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-red-500"></div>
                <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-yellow-500"></div>
                <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-green-500"></div>
              </div>
              <div className="tw-flex-1 tw-text-center tw-text-xs tw-text-gray-400">renewmytags.com/payment-methods</div>
            </div>
            <div className="tw-p-6">
              <div className="tw-flex tw-gap-1 tw-bg-gray-200 tw-rounded-lg tw-p-1 tw-mb-5 tw-w-fit">
                <div className="tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-bg-white tw-rounded-md tw-shadow-sm">ACH / eCheck</div>
                <div className="tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500">Credit / Debit</div>
              </div>
              <div className="tw-space-y-3">
                {[{ type: "Checking", num: "••••2345", routing: "314074269", isDefault: true }, { type: "Savings", num: "••••8901", routing: "021000021", isDefault: false }].map((m, i) => (
                  <div key={i} className={`tw-flex tw-items-center tw-justify-between tw-p-3 tw-rounded-lg tw-border ${m.isDefault ? 'tw-border-green-300 tw-bg-green-50' : 'tw-border-gray-200'}`}>
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center ${m.isDefault ? 'tw-bg-green-100 tw-text-green-600' : 'tw-bg-gray-100 tw-text-gray-500'}`}>
                        <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15A2.25 2.25 0 002.25 6.75v10.5A2.25 2.25 0 004.5 19.5z" /></svg>
                      </div>
                      <div>
                        <div className="tw-text-sm tw-font-medium tw-text-gray-900">{m.type} {m.num}</div>
                        <div className="tw-text-xs tw-text-gray-500">Routing: {m.routing}</div>
                      </div>
                    </div>
                    {m.isDefault && <span className="tw-text-xs tw-font-medium tw-text-green-700 tw-bg-green-100 tw-px-2 tw-py-0.5 tw-rounded-full">Default</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="tw-order-1 lg:tw-order-2">
            <div className="tw-text-sm tw-font-semibold tw-text-primary-600 tw-uppercase tw-tracking-wide tw-mb-3">Flexible Payments</div>
            <h2 className="tw-text-3xl md:tw-text-4xl tw-text-gray-900 tw-m-0 tw-mb-4">Pay your way, securely</h2>
            <p className="tw-text-lg tw-text-gray-600 tw-m-0 tw-mb-6 tw-leading-relaxed">
              Add ACH/eCheck or credit/debit cards. Set a default payment method and we'll handle the rest when your renewal comes due. Switch methods anytime.
            </p>
            <ul className="tw-space-y-3 tw-m-0 tw-p-0 tw-list-none">
              {["ACH, debit, and credit card support", "Set a default method or choose per plate", "Email and SMS notifications before every charge"].map((item, i) => (
                <li key={i} className="tw-flex tw-items-start tw-gap-3">
                  <svg className="tw-w-5 tw-h-5 tw-text-green-500 tw-mt-0.5 tw-flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <span className="tw-text-sm tw-text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Renewal History Preview */}
      <Section background="tw-bg-gray-50" padding="tw-py-16 md:tw-py-24">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-12 tw-items-center">
          <div>
            <div className="tw-text-sm tw-font-semibold tw-text-primary-600 tw-uppercase tw-tracking-wide tw-mb-3">Full Transparency</div>
            <h2 className="tw-text-3xl md:tw-text-4xl tw-text-gray-900 tw-m-0 tw-mb-4">Complete renewal history</h2>
            <p className="tw-text-lg tw-text-gray-600 tw-m-0 tw-mb-6 tw-leading-relaxed">
              Every renewal is logged with transaction details, amounts, and status. View your complete history for any plate at any time — no more guessing when you last renewed.
            </p>
            <ul className="tw-space-y-3 tw-m-0 tw-p-0 tw-list-none">
              {["Full transaction history with dates and amounts", "Payment status tracking (completed, pending, processing)", "Exportable records for your personal files"].map((item, i) => (
                <li key={i} className="tw-flex tw-items-start tw-gap-3">
                  <svg className="tw-w-5 tw-h-5 tw-text-green-500 tw-mt-0.5 tw-flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <span className="tw-text-sm tw-text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          {/* History mockup */}
          <div className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-border tw-border-gray-200 tw-overflow-hidden">
            <div className="tw-bg-gray-900 tw-px-4 tw-py-3 tw-flex tw-items-center tw-gap-2">
              <div className="tw-flex tw-gap-1.5">
                <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-red-500"></div>
                <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-yellow-500"></div>
                <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-green-500"></div>
              </div>
              <div className="tw-flex-1 tw-text-center tw-text-xs tw-text-gray-400">renewmytags.com/renewal-history/ABC1234</div>
            </div>
            <div className="tw-p-6">
              <h3 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0 tw-mb-4">Renewal History — <span className="tw-text-blue-600">ABC1234</span></h3>
              <div className="tw-space-y-3">
                {[
                  { date: "Mar 15, 2026", amount: "$85.50", status: "Completed", method: "ACH", county: "Miami-Dade" },
                  { date: "Mar 12, 2025", amount: "$82.00", status: "Completed", method: "ACH", county: "Miami-Dade" },
                  { date: "Mar 10, 2024", amount: "$79.50", status: "Completed", method: "Credit", county: "Miami-Dade" },
                ].map((r, i) => (
                  <div key={i} className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-rounded-lg tw-bg-gray-50 tw-border tw-border-gray-100">
                    <div>
                      <div className="tw-text-sm tw-font-medium tw-text-gray-900">{r.date}</div>
                      <div className="tw-text-xs tw-text-gray-500">{r.county} · {r.method}</div>
                    </div>
                    <div className="tw-text-right">
                      <div className="tw-text-sm tw-font-semibold tw-text-gray-900">{r.amount}</div>
                      <span className="tw-text-xs tw-font-medium tw-text-green-700 tw-bg-green-100 tw-px-2 tw-py-0.5 tw-rounded-full">{r.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Features */}
      <Section padding="tw-py-16 md:tw-py-24">
        <div className="tw-text-center tw-mb-12">
          <h2 className="tw-text-4xl tw-text-gray-900 tw-m-0">Why Choose RMT</h2>
          <p className="tw-text-lg tw-text-gray-500 tw-mt-3 tw-mb-0">
            Built for vehicle owners who value their time.
          </p>
        </div>
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6">
          {[
            {
              icon: faClock,
              title: "Tag Renewal Made Easy",
              description: "Streamline your tag renewal process with our user-friendly service. Never miss a renewal deadline.",
            },
            {
              icon: faBell,
              title: "Automatic Renewals",
              description: "Never miss a tag renewal again. Stay on the road hassle-free with automated processing and timely reminders.",
            },
            {
              icon: faShieldHalved,
              title: "Secure & Private",
              description: "Your data is protected with bank-level encryption. We never share your personal information.",
            },
          ].map((feature, idx) => (
            <Card key={idx} variant="elevated" className="tw-text-center hover:tw-shadow-xl tw-transition-shadow tw-duration-300">
              <div className="tw-w-14 tw-h-14 tw-rounded-xl tw-bg-primary-50 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-5">
                <FontAwesomeIcon icon={feature.icon} className="tw-text-primary-500 tw-text-xl" />
              </div>
              <h3 className="tw-text-xl tw-text-gray-900 tw-m-0 tw-mb-2">{feature.title}</h3>
              <p className="tw-text-sm tw-text-gray-500 tw-m-0 tw-leading-relaxed">{feature.description}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* CTA Banner */}
      <Section background="tw-bg-gray-900" padding="tw-py-16 md:tw-py-20">
        <div className="tw-text-center">
          <h2 className="tw-text-4xl tw-text-white tw-m-0">
            Ready to automate your tag renewal?
          </h2>
          <p className="tw-text-lg tw-text-gray-400 tw-mt-4 tw-mb-8">
            Join thousands of vehicle owners who never worry about expired tags.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="tw-bg-white tw-text-gray-900 tw-font-semibold tw-px-10 tw-py-4 tw-rounded-lg tw-text-lg tw-border-0 tw-cursor-pointer tw-transition-all tw-duration-200 tw-shadow-md hover:tw-shadow-lg hover:tw-bg-gray-100"
          >
            Get Started Now
            <FontAwesomeIcon icon={faArrowRight} className="tw-ml-2" />
          </button>
        </div>
      </Section>

      <PublicFooter />
    </div>
  );
};

export default Home;
